import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ImageUploaderComponent } from '../../../../../shared/image-uploader/image-uploader.component';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { DriversManagementApiService } from '../../../services/drivers-management-api.service';
import { CreateDriverPayload, UpdateDriverPayload } from '../../../models/driver-payload';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';

@Component({
  selector: 'app-driver-form-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ImageUploaderComponent, TranslateModule],
  templateUrl: './driver-form-page.component.html',
})
export class DriverFormPageComponent implements OnInit {

  private apiService = inject(DriversManagementApiService);
  private apiErrorService = inject(ApiErrorService);
  private toast = inject(ToastNotificationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  languageService = inject(LanguageService);

  driverId: string | null = null;
  disableButtons = false;
  loadingExisting = false;
  submitLoading = false;
  fieldErrors: Record<string, string[]> = {};

  form: CreateDriverPayload = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    first_name_ar: '',
    last_name_ar: '',
    phone_number: '',
    profile_image: null,
  };

  get isEditMode(): boolean { return this.driverId !== null; }

  ngOnInit(): void {
    this.driverId = this.route.snapshot.paramMap.get('id');
    if (this.driverId) this.loadExisting(this.driverId);
  }

  private loadExisting(id: string): void {
    this.loadingExisting = true;
    this.apiService.getDriverById(id).subscribe({
      next: (driver) => {
        this.form = {
          email: driver.email ?? '',
          password: '',
          first_name: driver.first_name ?? '',
          last_name: driver.last_name ?? '',
          first_name_ar: driver.first_name_ar ?? '',
          last_name_ar: driver.last_name_ar ?? '',
          phone_number: driver.phone_number ?? '',
          profile_image: driver.profile_image ?? null,
        };
        this.loadingExisting = false;
      },
      error: () => {
        this.loadingExisting = false;
        this.toast.createNotification(this.translate.instant('drivers_management.form.errors.load_message'), ToastNotificationType.ERROR, this.translate.instant('drivers_management.form.errors.load_title'), ToastPosition.TOP_CENTER);
      }
    });
  }

  onProfileImageChange(images: string[]): void {
    this.form.profile_image = images[0] ?? null;
  }

  submit(ngForm: NgForm): void {
    if (ngForm.invalid) {
      ngForm.control.markAllAsTouched();
      this.toast.createNotification(this.translate.instant('drivers_management.form.errors.missing_fields'), ToastNotificationType.ERROR, "", ToastPosition.TOP_CENTER);
      return;
    }

    this.fieldErrors = {};
    this.disableButtons = true;
    this.submitLoading = true;

    if (this.isEditMode && this.driverId) {
      const payload: UpdateDriverPayload = { ...this.form };
      if (!payload.password) delete payload.password;
      this.apiService.updateDriver(this.driverId, payload).subscribe({
        next: () => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.toast.createNotification(this.translate.instant('drivers_management.form.success.saved_message'), ToastNotificationType.SUCCESS, this.translate.instant('drivers_management.form.success.saved_title'), ToastPosition.BOTTOM_RIGHT);
          this.router.navigate(['/drivers']);
        },
        error: (err) => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.applyApiErrors(err, this.translate.instant('drivers_management.form.errors.save_failed'));
        }
      });
    } else {
      this.apiService.createDriver(this.form).subscribe({
        next: () => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.toast.createNotification(this.translate.instant('drivers_management.form.success.created_message'), ToastNotificationType.SUCCESS, this.translate.instant('drivers_management.form.success.created_title'), ToastPosition.BOTTOM_RIGHT);
          this.router.navigate(['/drivers']);
        },
        error: (err) => {
          this.disableButtons = false;
          this.submitLoading = false;
          this.applyApiErrors(err, this.translate.instant('drivers_management.form.errors.create_failed'));
        }
      });
    }
  }

  private applyApiErrors(err: any, fallbackMessage: string): void {
    const fieldErrors = this.apiErrorService.getFieldErrors(err);
    if (Object.keys(fieldErrors).length > 0) {
      this.fieldErrors = fieldErrors;
      return;
    }
    this.toast.createNotification(this.apiErrorService.getDisplayMessage(err, fallbackMessage), ToastNotificationType.ERROR);
  }

  clearFieldError(field: string): void {
    if (this.fieldErrors[field]) {
      const { [field]: _, ...rest } = this.fieldErrors;
      this.fieldErrors = rest;
    }
  }

  cancel(): void {
    this.router.navigate(['/drivers']);
  }
}
